<template>
  <!--
    추천 목록이 있는 입력창.
    클릭하면 options 를 펼쳐 고를 수 있고, 목록에 없는 값은 그대로 입력한다 (값은 자유 텍스트).
    선택창 + '직접입력' 입력창을 나란히 두던 방식을 대신한다.
  -->
  <ml-input v-if="isPrint" :is-print="true" :value="value" />
  <div
    v-else
    ref="root"
    class="suggest-input"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
    @keydown="onKeydown"
  >
    <ml-input
      :value="value"
      :readonly="readonly"
      :disabled="disabled"
      :placeholder="placeholder"
      :max="max"
      autocomplete="off"
      @input="onInput"
      @click="open"
    />
    <span
      v-if="!readonly && !disabled"
      class="suggest-input__toggle"
      @mousedown.prevent="toggle"
    >
      <feather-icon :icon="isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" svg-classes="w-4 h-4" />
    </span>
    <ul v-if="isOpen && filteredOptions.length" class="suggest-input__list" role="listbox">
      <li
        v-for="(option, index) in filteredOptions"
        :key="option"
        role="option"
        :aria-selected="option === value"
        :class="{ active: index === activeIndex, selected: option === value }"
        @mousedown.prevent="select(option)"
        @mouseenter="activeIndex = index"
      >
        {{ $t(option) }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SuggestInput",
  props: {
    value: { type: String, default: "" },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: "선택하거나 직접 입력" },
    readonly: Boolean,
    disabled: Boolean,
    isPrint: Boolean,
    max: { type: Number, default: 200 },
  },
  data() {
    return {
      isOpen: false,
      // 입력해서 목록을 좁히는 중인지. 처음 펼칠 때는 현재 값과 관계없이 전체를 보여 준다
      isFiltering: false,
      activeIndex: -1,
    };
  },
  computed: {
    filteredOptions() {
      const keyword = (this.value || "").trim();
      if (!this.isFiltering || !keyword) return this.options;
      return this.options.filter((option) => option.includes(keyword) || this.$t(option).includes(keyword));
    },
  },
  methods: {
    open() {
      if (this.readonly || this.disabled) return;
      this.isOpen = true;
      this.isFiltering = false;
      this.activeIndex = this.options.indexOf(this.value);
    },
    close() {
      this.isOpen = false;
      this.isFiltering = false;
      this.activeIndex = -1;
    },
    toggle() {
      if (this.isOpen) {
        this.close();
        return;
      }
      const input = this.$refs.root && this.$refs.root.querySelector("input");
      if (input) input.focus();
      this.open();
    },
    select(option) {
      this.$emit("input", option);
      this.$emit("change", option);
      this.close();
    },
    onInput(value) {
      if (value === this.value) return;
      this.$emit("input", value);
      this.$emit("change", value);
      if (this.readonly || this.disabled) return;
      this.isOpen = true;
      this.isFiltering = true;
      this.activeIndex = -1;
    },
    onFocusIn() {
      this.open();
    },
    onFocusOut(event) {
      // 목록 안으로 포커스가 옮겨 가는 경우는 닫지 않는다
      if (this.$refs.root && this.$refs.root.contains(event.relatedTarget)) return;
      this.close();
    },
    onKeydown(event) {
      if (this.readonly || this.disabled) return;
      const count = this.filteredOptions.length;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!this.isOpen) this.open();
        else if (count) this.activeIndex = (this.activeIndex + 1) % count;
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        if (!this.isOpen) this.open();
        else if (count) this.activeIndex = (this.activeIndex - 1 + count) % count;
      } else if (event.key === "Enter") {
        if (this.isOpen && this.activeIndex >= 0 && this.activeIndex < count) {
          event.preventDefault();
          this.select(this.filteredOptions[this.activeIndex]);
        }
      } else if (event.key === "Escape" || event.key === "Tab") {
        this.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.suggest-input {
  position: relative;
  width: 100%;

  // 펼침 아이콘 자리만큼 입력 글자가 아이콘에 가리지 않게 한다
  &::v-deep input {
    padding-right: 32px !important;
  }
}

.suggest-input__toggle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7a7a7a;
  cursor: pointer;
}

.suggest-input__list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100;
  max-height: 240px;
  overflow-y: auto;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

  li {
    padding: 8px 12px;
    font-size: 14px;
    line-height: 1.4;
    cursor: pointer;
    word-break: keep-all;

    &.active {
      background: rgba(var(--vs-primary), 0.08);
    }
    &.selected {
      color: rgba(var(--vs-primary), 1);
      font-weight: 600;
    }
  }
}
</style>
